import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { TAB_SLUGS } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { Callout } from '../Callout.js';
import { SideDialog } from './SideDialog.js';
import { JoinLinkPanel } from './JoinLinkPanel.js';
import { RoleSelect, inviteRefusal, roleMeaning } from './roles.js';

/** The two ways to invite (2026-11-02): one address at a time, or one link for a whole team. */
export type InviteMethod = 'email' | 'link';

export interface InvitePrefill {
  readonly email: string;
  readonly role: string;
  readonly credit: string | null;
}

/**
 * INVITE A TEAMMATE: an address, the job, and the credit if there is one.
 *
 * The role is asked as the job they will do, and what that job may change is
 * said under the picker before the invitation goes out, rather than after
 * somebody asks why they cannot do something.
 *
 * A production whose plan has no collaboration module is told so here, where
 * the producer is trying to invite, with the way to Plan & billing — the gate is
 * on inviting, never on the team that already exists.
 *
 * TWO METHODS, ONE PANEL (owner brief 2026-11-02, §2): "Invite by email", as it
 * always was, and "Share invite link" (`JoinLinkPanel`), one link anyone may use
 * to ASK to join — the owner approves each request. The method is in the address
 * (`?invite=1&method=link`) like every other state of this page; each method
 * shows its own single primary button, never both.
 */
export function InviteDrawer({
  projectId,
  prefill,
  planAllowsInvites,
  method = 'email',
  onMethod,
  onClose,
  onSent,
}: {
  projectId: string;
  prefill: InvitePrefill | null;
  planAllowsInvites: boolean;
  method?: InviteMethod;
  onMethod?: (method: InviteMethod) => void;
  onClose: () => void;
  onSent: (email: string) => Promise<void> | void;
}) {
  const [email, setEmail] = useState(prefill?.email ?? '');
  // The most common hire on this product's surface, and a safe one: a location
  // manager can source and select, and cannot touch the project record or the
  // team.
  const [role, setRole] = useState(prefill?.role ?? 'LOCATION_MANAGER');
  const [credit, setCredit] = useState(prefill?.credit ?? '');
  const [error, setError] = useState<string | null>(null);
  // The Team tab's own address with its last segment swapped: the same
  // production's Plan & billing, however the production is addressed.
  const { pathname } = useLocation();
  const billingHref = pathname.replace(/\/[^/]+\/?$/, `/${TAB_SLUGS.BILLING}`);

  const invite = useMutation({
    mutationFn: () =>
      api.invite(projectId, {
        email: email.trim(),
        authorizationRole: role,
        ...(credit.trim() ? { productionTitle: credit.trim() } : {}),
      }),
    onSuccess: async (result) => {
      setError(null);
      await onSent(result.email);
    },
    onError: (e: unknown) => {
      setError(inviteRefusal(e));
    },
  });

  return (
    <SideDialog title="Invite a teammate" testId="invite-drawer" onClose={onClose}>
      <MethodSwitch method={method} onMethod={onMethod} />

      {method === 'link' ? (
        <JoinLinkPanel projectId={projectId} planAllowsInvites={planAllowsInvites} />
      ) : (
        <>
          <p className="team-dialog-lede">Add someone to this production and give them the right access.</p>

          {planAllowsInvites ? (
            <form
              className="team-form"
              data-testid="invite-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (email.trim() !== '' && !invite.isPending) invite.mutate();
              }}
            >
              <div className="team-field">
                <label className="flbl" htmlFor="invite-email">
                  Email
                </label>
                <input
                  id="invite-email"
                  data-testid="invite-email"
                  data-autofocus
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="colleague@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                />
              </div>

              <div className="team-field">
                <label className="flbl" htmlFor="invite-role">
                  Role on the production
                </label>
                <RoleSelect id="invite-role" testId="invite-role" value={role} onChange={setRole} />
                <p className="team-field-help" data-testid="invite-role-meaning">
                  {roleMeaning(role)}
                </p>
              </div>

              <div className="team-field">
                <label className="flbl" htmlFor="invite-title">
                  Credit (optional)
                </label>
                <input
                  id="invite-title"
                  data-testid="invite-title"
                  value={credit}
                  maxLength={255}
                  placeholder="e.g. 2nd Unit"
                  onChange={(e) => {
                    setCredit(e.target.value);
                  }}
                />
                <p className="team-field-help">How their name reads on a call sheet. It grants nothing.</p>
              </div>

              {/* Accepting does not grant the Production workspace (ADR 0010): an
                  invitee whose account ScoutAround has not enabled is refused until
                  it is. Said here, before the producer promises anyone a seat. */}
              <p className="team-field-help" data-testid="invite-access-note">
                Invitees can join only once ScoutAround has enabled the Production workspace for their account.
              </p>

              {error === null ? null : (
                <Callout tone="danger" title="Not invited" testId="invite-error">
                  {error}
                </Callout>
              )}

              <div className="team-dialog-actions">
                <button type="submit" className="btn btn-primary" data-testid="invite-submit" disabled={invite.isPending}>
                  {invite.isPending ? 'Sending…' : 'Send invitation'}
                </button>
                <button type="button" className="ctl" data-testid="invite-cancel" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <Callout
              tone="info"
              title="Inviting is part of the collaboration module"
              testId="invite-plan"
              action={
                <Link to={billingHref} className="ctl" data-testid="invite-plan-link" data-autofocus>
                  See Plan &amp; billing
                </Link>
              }
            >
              It is included from Lite upwards. Everyone already on this production stays on it.
            </Callout>
          )}
        </>
      )}
    </SideDialog>
  );
}

const METHODS: readonly { id: InviteMethod; label: string }[] = [
  { id: 'email', label: 'Invite by email' },
  { id: 'link', label: 'Share invite link' },
];

/** The two methods as a pair of tabs; arrows move between them. Not a primary: a choice. */
function MethodSwitch({ method, onMethod }: { method: InviteMethod; onMethod?: (method: InviteMethod) => void }) {
  return (
    <div
      className="team-method"
      role="tablist"
      aria-label="How to invite"
      onKeyDown={(event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = method === 'email' ? 'link' : 'email';
        onMethod?.(next);
        document.querySelector<HTMLElement>(`[data-testid="invite-method-${next}"]`)?.focus();
      }}
    >
      {METHODS.map((m) => (
        <button
          key={m.id}
          type="button"
          role="tab"
          aria-selected={method === m.id}
          tabIndex={method === m.id ? 0 : -1}
          className="team-method-option"
          data-testid={`invite-method-${m.id}`}
          onClick={() => {
            onMethod?.(m.id);
          }}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}
